import React, { useState } from 'react';
import { LayoutDashboard, Send, AlertTriangle, Settings, LogOut, ShieldCheck, FileCheck, ChevronLeft, ChevronRight, Plug, Sliders, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export type Page = 'dashboard' | 'workbench' | 'outbox' | 'ledger' | 'friction' | 'checks' | 'settings-integrations' | 'settings-rules';
interface LayoutProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  children: React.ReactNode;
}
export function Layout({
  currentPage,
  onNavigate,
  children
}: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(currentPage === 'settings-integrations' || currentPage === 'settings-rules');
  const navItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'checks',
    label: 'Checks',
    icon: FileCheck
  }, {
    id: 'outbox',
    label: 'Outbox',
    icon: Send
  }, {
    id: 'friction',
    label: 'Needs Review',
    icon: AlertTriangle
  }];
  const settingsItems = [{
    id: 'settings-integrations',
    label: 'Integrations',
    icon: Plug
  }, {
    id: 'settings-rules',
    label: 'Rules',
    icon: Sliders
  }];
  const isSettingsActive = currentPage === 'settings-integrations' || currentPage === 'settings-rules';
  return <div className="flex h-screen bg-slate-50 overflow-hidden" data-id="element-32">
      <motion.aside animate={{
      width: collapsed ? 64 : 256
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} className="bg-[#0F172A] text-slate-300 flex flex-col flex-shrink-0 border-r border-slate-800 overflow-visible relative" data-id="element-33">
        {/* Logo + collapse toggle */}
        <div className="p-4 flex items-center border-b border-slate-800/50 min-h-[65px]" data-id="element-34">
          <div className="bg-emerald-500/10 p-2 rounded-lg flex-shrink-0" data-id="element-35">
            <ShieldCheck className="w-6 h-6 text-emerald-400" data-id="element-36" />
          </div>
          {!collapsed && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="ml-3 flex-1 min-w-0" data-id="element-37">
              <h1 className="text-white font-bold text-lg tracking-tight whitespace-nowrap" data-id="element-38">
                ZenClaims
              </h1>
            </motion.div>}
          {!collapsed && <button onClick={() => setCollapsed(true)} className="flex-shrink-0 ml-auto p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800/50 transition-colors" data-id="element-39">
              <ChevronLeft className="w-4 h-4" data-id="element-40" />
            </button>}
        </div>

        {collapsed && <button onClick={() => setCollapsed(false)} className="absolute -right-3 top-[30px] z-20 w-6 h-6 bg-[#0F172A] border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-500 transition-colors shadow-md" data-id="element-41">
            <ChevronRight className="w-3 h-3" data-id="element-42" />
          </button>}

        <nav className="flex-1 px-2 py-6 space-y-1 overflow-y-auto overflow-x-hidden" data-id="element-43">
          {navItems.map(item => {
          const isActive = currentPage === item.id;
          return <button key={item.id} onClick={() => onNavigate(item.id as Page)} title={collapsed ? item.label : undefined} className={`w-full flex items-center px-2.5 py-2.5 rounded-lg transition-all duration-200 group relative ${collapsed ? 'justify-center' : 'space-x-3'} ${isActive ? 'text-white bg-slate-800 shadow-lg shadow-black/20' : 'hover:text-white hover:bg-slate-800/50'}`} data-id="element-44">
                {isActive && <motion.div layoutId="activeTab" className="absolute left-0 w-1 h-6 bg-emerald-500 rounded-r-full" initial={false} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-45" />}
                <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-emerald-400' : 'text-slate-400 group-hover:text-slate-300'}`} data-id="element-46" />
                {!collapsed && <span className="font-medium text-sm whitespace-nowrap" data-id="element-47">
                    {item.label}
                  </span>}
                {!collapsed && item.id === 'outbox' && <span className="ml-auto bg-emerald-500/20 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-48">
                    5
                  </span>}
                {!collapsed && item.id === 'friction' && <span className="ml-auto bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-49">
                    3
                  </span>}
              </button>;
        })}
        </nav>

        <div className="p-3 border-t border-slate-800/50" data-id="element-50">
          {/* Settings expandable */}
          <button title={collapsed ? 'Settings' : undefined} onClick={() => {
          if (collapsed) {
            onNavigate('settings-integrations');
          } else {
            setSettingsOpen(o => !o);
          }
        }} className={`flex items-center px-2.5 py-2 w-full rounded-lg transition-colors ${collapsed ? 'justify-center' : 'space-x-3'} ${isSettingsActive ? 'text-white bg-slate-800' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`} data-id="element-51">
            <Settings className="w-5 h-5 flex-shrink-0" data-id="element-52" />
            {!collapsed && <>
                <span className="text-sm font-medium flex-1 text-left" data-id="element-53">
                  Settings
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} data-id="element-54" />
              </>}
          </button>

          {/* Settings sub-items */}
          <AnimatePresence data-id="element-55">
            {!collapsed && settingsOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden mt-1 ml-2 space-y-0.5" data-id="element-56">
                {settingsItems.map(item => {
              const isActive = currentPage === item.id;
              return <button key={item.id} onClick={() => onNavigate(item.id as Page)} className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-lg transition-colors text-sm relative ${isActive ? 'text-white bg-slate-800' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`} data-id="element-57">
                      {isActive && <motion.div layoutId="activeTab" className="absolute left-0 w-1 h-5 bg-emerald-500 rounded-r-full" initial={false} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }} data-id="element-58" />}
                      <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-59" />
                      <span className="font-medium whitespace-nowrap" data-id="element-60">
                        {item.label}
                      </span>
                    </button>;
            })}
              </motion.div>}
          </AnimatePresence>

          <div className={`mt-3 flex items-center px-2.5 py-2 ${collapsed ? 'justify-center' : 'space-x-3'}`} data-id="element-61">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-white flex-shrink-0" data-id="element-62">
              LA
            </div>
            {!collapsed && <div className="flex-1 min-w-0" data-id="element-63">
                <p className="text-sm font-medium text-white truncate" data-id="element-64">
                  Lenny A.
                </p>
                <p className="text-xs text-slate-500 truncate" data-id="element-65">AR Analyst</p>
              </div>}
            {!collapsed && <LogOut className="w-4 h-4 text-slate-500 hover:text-white cursor-pointer flex-shrink-0" data-id="element-66" />}
          </div>
        </div>
      </motion.aside>

      <main className="flex-1 overflow-hidden flex flex-col relative" data-id="element-67">
        <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-id="element-68">
          <motion.div key={currentPage} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="max-w-7xl mx-auto" data-id="element-69">
            {children}
          </motion.div>
        </div>
      </main>
    </div>;
}